import React, { useCallback, useEffect, useRef, useState } from "react";

// Forced clock-in: prove it is really the employee (a shared password is not enough),
// for clocking in AND clocking out (`action` "in" | "out").
// Face check first (photo + one head turn, matched to the logged-in person's own
// registered face); no webcam / no registered face → their 4-digit kiosk PIN.
// `api(path, body)` posts to the API and resolves `data`, or throws an Error with `.code`.
// Identical copy in admin, HRMS and Kanban.

const MOVE_TEXT = {
  left: "Slowly turn your head to the LEFT and hold",
  right: "Slowly turn your head to the RIGHT and hold",
  up: "Lift your chin and hold",
  down: "Lower your chin and hold",
};
const MOVE_ARROW = { left: "←", right: "→", up: "↑", down: "↓" };
const MOVE_MS = 2500;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const grab = (video) => {
  if (!video || !video.videoWidth) return null;
  const w = Math.min(640, video.videoWidth);
  const h = Math.round((video.videoHeight / video.videoWidth) * w);
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  c.getContext("2d").drawImage(video, 0, 0, w, h);
  return c.toDataURL("image/jpeg", 0.88);
};

export default function ClockInConfirm({ api, onDone, onCancel, action = "in" }) {
  const verb = action === "out" ? "clock out" : "clock in";
  // loading | face | pin | none | error
  const [mode, setMode] = useState("loading");
  const [check, setCheck] = useState(null);
  const [step, setStep] = useState(""); // countdown number or move text
  const [move, setMove] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pin, setPin] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const aliveRef = useRef(true);

  const stopCamera = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480, facingMode: "user" }, audio: false });
      if (!aliveRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return false;
      }
      streamRef.current = stream;
      return true;
    } catch (e) {
      return false; // no webcam or permission refused → PIN
    }
  };

  const load = useCallback(async () => {
    setMode("loading");
    setError("");
    setStep("");
    setMove(null);
    try {
      const c = await api("/attendanceSettings/me/clock-check", {});
      setCheck(c);
      if (c.face && (await startCamera())) return setMode("face");
      if (c.pin) return setMode("pin");
      setMode("none");
      if (c.pinLocked) setError("Your kiosk PIN is locked after too many wrong tries. Ask HR to set a new one.");
      else if (c.face) setError("No camera found and you have no kiosk PIN. Ask HR to set a PIN, or use a computer with a camera.");
    } catch (e) {
      setMode("error");
      setError(e.message || "Could not reach the server. Try again.");
    }
  }, [api]);

  useEffect(() => {
    aliveRef.current = true;
    load();
    return () => {
      aliveRef.current = false;
      stopCamera();
    };
  }, [load]);

  // attach the stream once the <video> is on screen
  useEffect(() => {
    if (mode === "face" && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [mode]);

  const runFace = async () => {
    setBusy(true);
    setError("");
    try {
      // a fresh challenge for every try (each one can be used once)
      let c = check;
      if (!c || !c.challengeToken || c.used) {
        c = await api("/attendanceSettings/me/clock-check", {});
        setCheck(c);
        if (!c.face) throw Object.assign(new Error("Face check is not available right now. Use your PIN."), { code: "face_offline" });
      }
      setCheck({ ...c, used: true });
      for (const n of [3, 2, 1]) {
        setStep(String(n));
        await sleep(700);
      }
      setStep("Look straight at the camera");
      const image = grab(videoRef.current);
      if (!image) throw new Error("Camera not ready — try again");
      const challengeImages = [];
      for (const s of c.steps || []) {
        setMove(s);
        setStep(MOVE_TEXT[s] || s);
        await sleep(MOVE_MS);
        const photo = grab(videoRef.current);
        if (!photo) throw new Error("Camera not ready — try again");
        challengeImages.push(photo);
      }
      setMove(null);
      setStep("Checking…");
      const d = await api("/attendanceSettings/me/clock", { action, image, challengeToken: c.challengeToken, challengeImages });
      stopCamera();
      onDone(d);
    } catch (e) {
      setMove(null);
      setStep("");
      setError(e.message || "Face check failed — try again");
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  };

  const sendPin = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(pin)) return setError("Enter your 4-digit PIN");
    setBusy(true);
    setError("");
    try {
      const d = await api("/attendanceSettings/me/clock", { action, pin });
      stopCamera();
      onDone(d);
    } catch (err) {
      setPin("");
      setError(err.message || "Wrong PIN");
      if (err.code === "pin_locked") setMode("none");
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  };

  const usePin = () => {
    stopCamera();
    setError("");
    setMode("pin");
  };

  return (
    <div data-no-draft="1">
      <h5 style={s.title}>{action === "out" ? "Confirm it's you to clock out" : "Confirm it's you"}</h5>
      {mode === "loading" && <p style={s.text}>Preparing…</p>}

      {mode === "face" && (
        <>
          <p style={s.text}>Look at the camera. When asked, turn your head slowly.</p>
          <div style={s.camera}>
            <video ref={videoRef} playsInline muted style={s.video} />
            {check && check.registeredPhoto && (
              <div style={s.regBox} title="Your registered photo">
                <img src={check.registeredPhoto} alt="Registered" style={s.regImg} />
                <span style={s.regLabel}>Registered</span>
              </div>
            )}
            {move && <div style={{ ...s.arrow, ...(move === "left" ? { justifyContent: "flex-start" } : move === "right" ? { justifyContent: "flex-end" } : {}) }}>{MOVE_ARROW[move]}</div>}
            {step && <div style={s.strip}>{step}</div>}
          </div>
          {error && <p role="alert" style={s.error}>{error}</p>}
          <div style={s.row}>
            <button type="button" style={s.secondary} onClick={onCancel} disabled={busy}>Cancel</button>
            {check && check.pin && (
              <button type="button" style={s.secondary} onClick={usePin} disabled={busy}>Use PIN instead</button>
            )}
            <button type="button" style={s.primary} onClick={runFace} disabled={busy} autoFocus>
              {busy ? "Checking…" : error ? "Try again" : "Start face check"}
            </button>
          </div>
        </>
      )}

      {mode === "pin" && (
        <form onSubmit={sendPin}>
          {check && check.registeredPhoto && (
            <img src={check.registeredPhoto} alt="Your registered photo" title="Your registered photo" style={s.regPin} />
          )}
          <p style={s.text}>Enter your personal 4-digit kiosk PIN.</p>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            style={s.pin}
            aria-label="Kiosk PIN"
            autoFocus
          />
          {error && <p role="alert" style={s.error}>{error}</p>}
          <div style={s.row}>
            <button type="button" style={s.secondary} onClick={onCancel} disabled={busy}>Cancel</button>
            <button type="submit" style={s.primary} disabled={busy || pin.length !== 4}>{busy ? "Checking…" : action === "out" ? "Clock out" : "Clock in"}</button>
          </div>
        </form>
      )}

      {(mode === "none" || mode === "error") && (
        <>
          <p role="alert" style={s.error}>
            {error || `You have no registered face and no kiosk PIN, so you cannot ${verb} here. Ask HR to set your kiosk PIN or register your face.`}
          </p>
          <div style={s.row}>
            <button type="button" style={s.secondary} onClick={onCancel}>Back</button>
            {mode === "error" && <button type="button" style={s.primary} onClick={load}>Try again</button>}
          </div>
        </>
      )}
    </div>
  );
}

const s = {
  title: { margin: "0 0 8px", fontWeight: 600, fontSize: 18 },
  text: { margin: "0 0 12px", color: "#4b5563" },
  error: { color: "#b91c1c", margin: "8px 0 12px" },
  row: { display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", marginTop: 12 },
  primary: { border: 0, borderRadius: 8, padding: "8px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", background: "#1a7d3e", color: "#fff" },
  secondary: { border: 0, borderRadius: 8, padding: "8px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", background: "#eef2f6", color: "#1f2937" },
  camera: { position: "relative", width: "100%", aspectRatio: "4 / 3", background: "#000", borderRadius: 10, overflow: "hidden" },
  video: { width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" },
  arrow: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 6%", fontSize: 90, fontWeight: 800, color: "#facc15", textShadow: "0 4px 18px rgba(0,0,0,.7)", pointerEvents: "none" },
  strip: { position: "absolute", left: 0, right: 0, bottom: 0, padding: 8, background: "rgba(15,23,42,.8)", color: "#fff", fontSize: 15, textAlign: "center" },
  regBox: { position: "absolute", top: 8, left: 8, width: 76, background: "#fff", borderRadius: 8, padding: 3, boxShadow: "0 2px 8px rgba(0,0,0,.4)", textAlign: "center" },
  regImg: { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: 6, display: "block" },
  regLabel: { display: "block", fontSize: 10, fontWeight: 600, color: "#15803d", marginTop: 2 },
  regPin: { width: 84, height: 84, objectFit: "cover", borderRadius: "50%", border: "3px solid #15803d", display: "block", margin: "0 auto 10px" },
  pin: { width: 160, fontSize: 28, letterSpacing: 12, textAlign: "center", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 8 },
};
